import { format } from "date-fns";

import { useActiveLocations } from "features/locations/hooks/useActiveLocations";

import { buildDayAgenda, formatDuration, getServiceName } from "../../utils/build-day-agenda";
import { DayCell } from "./DayCell";
import styles from "./MonthView.module.css";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Distinguishable, non-semantic hues — kept well away from the green/amber
// already meaning "booked"/"time off" on the timeline, so a location dot is
// never mistaken for either of those.
const LOCATION_COLOR_PALETTE = [
  "#2563eb",
  "#7c3aed",
  "#0891b2",
  "#db2777",
  "#4d7c0f",
  "#b45309",
];

const buildLocationById = (locations) => {
  const sorted = [...(locations ?? [])].sort((a, b) => a.id - b.id);
  const byId = new Map();

  sorted.forEach((location, index) => {
    byId.set(location.id, {
      name: location.name,
      color: LOCATION_COLOR_PALETTE[index % LOCATION_COLOR_PALETTE.length],
    });
  });

  return byId;
};

// The whole visible month reduced to the handful of numbers worth putting up
// top: booked/free/off totals and a per-service breakdown. Re-runs each
// day's own buildDayAgenda rather than re-deriving the same figures another
// way, so these totals can never drift from what an individual day cell (or
// its agenda popover) shows for that same day.
//
// Service colours are assigned here, once for the whole month, over the
// alphabetical name order rather than encounter order — so a given service
// keeps the same colour across cells, across the service-mix bar, and across
// re-renders, regardless of which day happens to load its data first.
const buildMonthInsights = (cells) => {
  let bookedCount = 0;
  let freeMs = 0;
  let offMs = 0;
  const countByServiceName = new Map();

  cells.forEach((cell) => {
    if (!cell) return;

    buildDayAgenda(cell.doctor).forEach((block) => {
      const durationMs = block.end - block.start;

      if (block.kind === "booked") {
        bookedCount += 1;
        const serviceName = getServiceName(block.meeting);

        countByServiceName.set(serviceName, (countByServiceName.get(serviceName) ?? 0) + 1);
      } else if (block.kind === "off") {
        offMs += durationMs;
      } else {
        freeMs += durationMs;
      }
    });
  });

  const sortedNames = [...countByServiceName.keys()].sort((a, b) => a.localeCompare(b));
  const services = sortedNames.map((name, index) => ({
    name,
    count: countByServiceName.get(name),
    color: LOCATION_COLOR_PALETTE[index % LOCATION_COLOR_PALETTE.length],
  }));

  const topService = services.reduce(
    (best, service) => (!best || service.count > best.count ? service : best),
    null
  );

  return {
    bookedCount,
    freeMs,
    offMs,
    services,
    serviceByName: new Map(services.map((service) => [service.name, service])),
    topService,
  };
};

const buildMonthCells = (monthDate, dataByDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = firstOfMonth.getDay();

  const cells = Array.from({ length: leadingBlanks }, () => null);

  for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber++) {
    const date = new Date(year, month, dayNumber);
    const dateKey = format(date, "yyyy-MM-dd");

    cells.push({
      date,
      dateKey,
      // Exactly one doctor per day here — this view only ever renders for
      // "My Appointments", which is always scoped to a single doctor.
      doctor: dataByDate.get(dateKey)?.doctors?.[0],
    });
  }

  return cells;
};

// A full-month calendar grid built for a consultant's own schedule: each
// cell summarises the day (a booked/time-off timeline, a colour-coded pill
// per service/time-off, a handful of appointments each bordered in its own
// service's colour) rather than itemising every meeting, and clicking a day
// surfaces its complete agenda. See DayCell for the per-day rendering and
// utils/build-day-agenda for the timeline math.
export const MonthView = ({ data, selectedDate, onSelectDate }) => {
  const { data: locations } = useActiveLocations();
  const locationById = buildLocationById(locations);
  // A single-location practice has nothing to distinguish — only surface
  // location colour-coding once there's an actual choice being made.
  const showLocations = locationById.size > 1;

  const dataByDate = new Map((data ?? []).map((day) => [day.date, day]));
  const cells = buildMonthCells(selectedDate, dataByDate);

  const { bookedCount, freeMs, offMs, services, serviceByName, topService } =
    buildMonthInsights(cells);
  // Same reasoning as showLocations — a single service booked all month
  // gives the colour nothing to distinguish, so leave the default colour be.
  const showServices = serviceByName.size > 1;

  return (
    <div className={styles.wrapper}>
      <div className={styles.insightsPanel}>
        <div className={styles.insightStats}>
          <div className={`${styles.statCard} ${styles.statCardBooked}`}>
            <span className={styles.statLabel}>Booked</span>
            <span className={styles.statValue}>{bookedCount}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Available</span>
            <span className={styles.statValue}>{formatDuration(freeMs)}</span>
          </div>
          <div className={`${styles.statCard} ${styles.statCardOff}`}>
            <span className={styles.statLabel}>Time off</span>
            <span className={styles.statValue}>{formatDuration(offMs)}</span>
          </div>
          <div
            className={styles.statCard}
            style={topService ? { borderLeft: `3px solid ${topService.color}` } : undefined}
          >
            <span className={styles.statLabel}>Top service</span>
            {topService ? (
              <span className={styles.statTopServiceRow}>
                <span className={styles.statTopServiceName}>{topService.name}</span>
                <span className={styles.dayPill} style={{ background: topService.color }}>
                  {topService.count}
                </span>
              </span>
            ) : (
              <span className={styles.statValue}>—</span>
            )}
          </div>
        </div>

        {/* A single proportional bar plus a wrapping chip legend — the full
            service breakdown in a couple of lines, rather than a chart tall
            enough to compete with the calendar below it for space. */}
        {services.length > 0 && (
          <div className={styles.serviceMix}>
            <span className={styles.insightChartTitle}>By service</span>
            <div className={styles.serviceMixBar}>
              {services.map((service) => (
                <span
                  key={service.name}
                  className={styles.serviceMixSegment}
                  style={{ flexGrow: service.count, background: service.color }}
                  title={`${service.name}: ${service.count}`}
                />
              ))}
            </div>
            <div className={styles.serviceMixLegend}>
              {services.map((service) => (
                <span key={service.name} className={styles.legendItem}>
                  <span className={styles.serviceSwatch} style={{ background: service.color }} />
                  {service.name}
                  <span className={styles.dayPill} style={{ background: service.color }}>
                    {service.count}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDotBooked} />
          Booked
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDotOff} />
          Time off
        </span>
        {showLocations &&
          Array.from(locationById.values()).map((location) => (
            <span key={location.name} className={styles.legendItem}>
              <span className={styles.locationDot} style={{ background: location.color }} />
              {location.name}
            </span>
          ))}
      </div>

      <div className={styles.weekdayRow}>
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className={styles.weekdayLabel}>
            {label}
          </div>
        ))}
      </div>

      <div className={styles.grid}>
        {cells.map((cell, index) =>
          cell ? (
            <DayCell
              key={cell.dateKey}
              date={cell.date}
              doctor={cell.doctor}
              onSelectDate={onSelectDate}
              locationById={locationById}
              showLocations={showLocations}
              serviceByName={serviceByName}
              showServices={showServices}
            />
          ) : (
            <div key={`blank-${index}`} className={styles.dayCellBlank} />
          )
        )}
      </div>
    </div>
  );
};
